import React, { useState } from 'react';
import { ArrowRight, Building2, Upload, Globe, Phone, Hash } from 'lucide-react';

interface OrganizationInfoFormProps {
  initialData?: {
    companyName: string;
    companyLogo?: File;
    companyWebsite?: string;
    companyPhone?: string;
    companyABN?: string;
    companyACN?: string;
  };
  onSubmit: (data: {
    companyName: string;
    companyLogo?: File;
    companyWebsite?: string;
    companyPhone?: string;
    companyABN?: string;
    companyACN?: string;
  }) => void;
  isLoading: boolean;
}

export default function OrganizationInfoForm({ 
  initialData, 
  onSubmit, 
  isLoading 
}: OrganizationInfoFormProps) {
  const [formData, setFormData] = useState({
    companyName: initialData?.companyName || '',
    companyWebsite: initialData?.companyWebsite || '',
    companyPhone: initialData?.companyPhone || '',
    companyABN: initialData?.companyABN || '',
    companyACN: initialData?.companyACN || '',
  });

  const [logoFile, setLogoFile] = useState<File | null>(initialData?.companyLogo || null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    // Auto-add https:// to website if missing and validate format
    let website = formData.companyWebsite.trim();
    if (website) {
      // Auto-add https:// if no protocol is specified
      if (!website.match(/^https?:\/\//)) {
        website = `https://${website}`;
        // Update the form data with the corrected URL
        setFormData(prev => ({ ...prev, companyWebsite: website }));
      }
      
      // Validate the website format
      const websitePattern = /^https?:\/\/.+\..+/;
      if (!websitePattern.test(website)) {
        newErrors.companyWebsite = 'Please enter a valid website URL (e.g., https://example.com)';
      }
    }

    // Optional validation for ABN (11 digits)
    if (formData.companyABN && formData.companyABN.trim()) {
      const abnPattern = /^\d{11}$/;
      if (!abnPattern.test(formData.companyABN.replace(/\s/g, ''))) {
        newErrors.companyABN = 'ABN should be 11 digits';
      }
    }

    // Optional validation for ACN (9 digits)
    if (formData.companyACN && formData.companyACN.trim()) {
      const acnPattern = /^\d{9}$/;
      if (!acnPattern.test(formData.companyACN.replace(/\s/g, ''))) {
        newErrors.companyACN = 'ACN should be 9 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Process website URL (same logic as in validateForm)
      let processedWebsite = formData.companyWebsite.trim();
      if (processedWebsite && !processedWebsite.match(/^https?:\/\//)) {
        processedWebsite = `https://${processedWebsite}`;
      }
      
      onSubmit({
        companyName: formData.companyName.trim(),
        companyLogo: logoFile || undefined,
        companyWebsite: processedWebsite || undefined,
        companyPhone: formData.companyPhone.trim() || undefined,
        companyABN: formData.companyABN.replace(/\s/g, '') || undefined,
        companyACN: formData.companyACN.replace(/\s/g, '') || undefined,
      });
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'Image size should be less than 5MB' }));
        return;
      }

      setLogoFile(file);
      setErrors(prev => ({ ...prev, logo: '' }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatABN = (value: string) => {
    // Remove all non-digits and limit to 11 digits
    const digits = value.replace(/\D/g, '').substring(0, 11);
    // Format as XX XXX XXX XXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.substring(0, 2)} ${digits.substring(2)}`;
    if (digits.length <= 8) return `${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5)}`;
    return `${digits.substring(0, 2)} ${digits.substring(2, 5)} ${digits.substring(5, 8)} ${digits.substring(8)}`;
  };

  const formatACN = (value: string) => {
    // Remove all non-digits and limit to 9 digits
    const digits = value.replace(/\D/g, '').substring(0, 9);
    // Format as XXX XXX XXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.substring(0, 3)} ${digits.substring(3)}`;
    return `${digits.substring(0, 3)} ${digits.substring(3, 6)} ${digits.substring(6)}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center mx-auto mb-3">
          <Building2 className="h-6 w-6 text-green-600" />
        </div>
        <p className="text-muted-foreground">
          Tell us about your organization. This information can be updated later in your organization profile.
        </p>
      </div>

      {/* Company Name */}
      <div>
        <label htmlFor="companyName" className="block text-sm font-medium mb-1">
          Company Name <span className="text-red-500">*</span>
        </label>
        <input
          id="companyName"
          type="text"
          value={formData.companyName}
          onChange={(e) => handleInputChange('companyName', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.companyName ? 'border-red-500' : 'border-input'
          }`}
          placeholder="Enter your company name"
          disabled={isLoading}
        />
        {errors.companyName && (
          <p className="text-sm text-red-500 mt-1">{errors.companyName}</p>
        )}
      </div>

      {/* Company Logo */}
      <div>
        <label htmlFor="companyLogo" className="block text-sm font-medium mb-1">
          Company Logo (Optional)
        </label>
        <div className="flex items-center gap-4">
          {logoPreview ? (
            <img 
              src={logoPreview} 
              alt="Logo preview" 
              className="w-16 h-16 rounded-lg object-cover border border-input"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center border border-input">
              <Building2 className="h-6 w-6 text-gray-400" />
            </div>
          )}
          <div className="flex-1">
            <input
              id="companyLogo"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
              disabled={isLoading}
            />
            <label
              htmlFor="companyLogo"
              className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-md hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
            >
              <Upload className="h-4 w-4" />
              {logoFile ? 'Change Logo' : 'Upload Logo'}
            </label>
            <p className="text-sm text-muted-foreground mt-1">
              PNG, JPG or GIF up to 5MB
            </p>
          </div>
        </div>
        {errors.logo && (
          <p className="text-sm text-red-500 mt-1">{errors.logo}</p>
        )}
      </div>

      {/* Company Website */}
      <div>
        <label htmlFor="companyWebsite" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Company Website (Optional)
        </label>
        <input
          id="companyWebsite"
          type="url"
          value={formData.companyWebsite}
          onChange={(e) => handleInputChange('companyWebsite', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.companyWebsite ? 'border-red-500' : 'border-input'
          }`}
          placeholder="https://yourcompany.com"
          disabled={isLoading}
        />
        {errors.companyWebsite && (
          <p className="text-sm text-red-500 mt-1">{errors.companyWebsite}</p>
        )}
      </div>

      {/* Company Phone */}
      <div>
        <label htmlFor="companyPhone" className="block text-sm font-medium mb-1 flex items-center gap-2">
          <Phone className="h-4 w-4" />
          Company Phone (Optional)
        </label>
        <input
          id="companyPhone"
          type="tel"
          value={formData.companyPhone}
          onChange={(e) => handleInputChange('companyPhone', e.target.value)}
          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="+61 2 1234 5678"
          disabled={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ABN */}
        <div>
          <label htmlFor="companyABN" className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Hash className="h-4 w-4" />
            ABN (Optional)
          </label>
          <input
            id="companyABN"
            type="text"
            value={formData.companyABN}
            onChange={(e) => handleInputChange('companyABN', formatABN(e.target.value))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.companyABN ? 'border-red-500' : 'border-input'
            }`}
            placeholder="12 345 678 901"
            disabled={isLoading}
          />
          {errors.companyABN && (
            <p className="text-sm text-red-500 mt-1">{errors.companyABN}</p>
          )}
        </div>

        {/* ACN */}
        <div>
          <label htmlFor="companyACN" className="block text-sm font-medium mb-1 flex items-center gap-2">
            <Hash className="h-4 w-4" />
            ACN (Optional)
          </label>
          <input
            id="companyACN"
            type="text"
            value={formData.companyACN}
            onChange={(e) => handleInputChange('companyACN', formatACN(e.target.value))}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
              errors.companyACN ? 'border-red-500' : 'border-input'
            }`}
            placeholder="123 456 789"
            disabled={isLoading}
          />
          {errors.companyACN && (
            <p className="text-sm text-red-500 mt-1">{errors.companyACN}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next: Billing Information
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
